'use client'

import type { Job } from '@/types/job'

import { useParams } from 'next/navigation'
import { i18n, type Locale } from '@/i18n'
import { useEffect, useState } from 'react'

import DetailCard from '@/components/atoms/DetailCard/DetailCard'

type JobDetailCardProps = Job

export default function JobDetailCard({
  detailsLabel,
  startDate,
  startDateLabel,
  endDate,
  duration,
  durationLabel,
  location,
  locationLabel,
  deadlineLabel,
  deadlineDate,
  applyLink,
}: JobDetailCardProps) {
  const { city, country } = location || {}
  const locationString =
    city?.title && country?.title
      ? `${city.title}, ${country.title}`
      : city?.title || country?.title || '-'

  const { locale } = useParams() as { locale: Locale }
  const dateLocale = i18n.dateLocales[locale] || 'en-GB'

  const [formattedStartDate, setFormattedStartDate] = useState<string | undefined>(undefined)
  const [formattedEndDate, setFormattedEndDate] = useState<string | undefined>(undefined)
  const [formattedDeadlineDate, setFormattedDeadlineDate] = useState<string | undefined>(undefined)

  // Set formatted dates - get the user's timezone from window.Intl
  useEffect(() => {
    const timeZone = window.Intl.DateTimeFormat().resolvedOptions().timeZone
    function formatDate(date: number | string | null | undefined, includeTime: boolean = false) {
      if (!date || typeof date === 'string') return undefined
      return (
        new Date(date).toLocaleDateString(dateLocale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone,
          hour: includeTime ? 'numeric' : undefined,
          minute: includeTime ? 'numeric' : undefined,
        }) || undefined
      )
    }
    setFormattedEndDate(formatDate(endDate))
    setFormattedStartDate(formatDate(startDate))
    setFormattedDeadlineDate(formatDate(deadlineDate, true))
  }, [endDate, startDate, deadlineDate, dateLocale])

  return (
    <DetailCard
      headline={detailsLabel}
      details={[
        {
          label: startDateLabel,
          value: typeof startDate === 'string' ? startDate : formattedStartDate || undefined,
        },
        {
          label: durationLabel,
          value:
            duration && formattedStartDate && formattedEndDate
              ? `${duration} (${formattedStartDate} - ${formattedEndDate})`
              : duration,
        },
        {
          label: locationLabel,
          value: locationString,
        },
        {
          label: deadlineLabel,
          value: typeof deadlineDate === 'string' ? deadlineDate : formattedDeadlineDate || '-',
        },
        { value: applyLink },
      ]}
    />
  )
}
